import axios from "axios";
import Parse from "parse";

/**
 * Tear the session down, on the server and here.
 *
 * Lifted out of App so it can be tested. The hard part is not any one step but the
 * order surviving failure: the logout request rejects precisely when the session is
 * already dead, which is exactly when the clearing below matters most. Returning on
 * that rejection is the bug this was extracted to cover — it left a user on a page
 * whose every call came back 141, with their tokens still in storage.
 */
export async function endSession(): Promise<void> {
  try {
    const token = localStorage.getItem("sessionToken");
    // Sent so the server can revoke it. Destroying the Parse session does not cover
    // the refresh token: rotation needs no live session, so an unrevoked one keeps
    // minting access tokens until it reaches the absolute session ceiling.
    const refreshToken = localStorage.getItem("refreshToken");
    if (token) {
      await axios.post(
        `${process.env.REACT_APP_PARSE_SERVER_URL}/auth/logout`,
        { refreshToken },
        {
          headers: {
            "x-parse-session-token": token,
          },
        }
      );
    }
  } catch (error) {
    // Logged, not surfaced, and above all not returned on. There is also nothing to
    // try again: the server revokes the refresh token before it looks the session up,
    // so that half is already done.
    console.error("Error during logout:", error);
  }

  localStorage.removeItem("sessionToken");
  localStorage.removeItem("authJwt");
  localStorage.removeItem("refreshToken");
  // The bearer rides on every Parse request from here, not from storage. Left set, the
  // login page's own calls go out carrying a token that is already dead.
  Parse.CoreManager.set("REQUEST_HEADERS", {});

  // Clear the Parse SDK's own cached session so a stale/destroyed sessionToken
  // can't be reused by a later Parse.User.become() race.
  await Parse.User.logOut().catch(() => {});
}
